import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import PropTypes from "prop-types";
import { fetchAuthJson, login as apiLogin } from "./api";
import { apiBase } from "./apiBase";

const AUTH_ME_PATH = "auth/session";
const AUTH_LOGOUT_PATH = "auth/logout";

function deriveUser(payload) {
    if (!payload || typeof payload !== "object") return null;
    const candidate =
        payload.user ||
        (payload.user_id ? { id: payload.user_id, user_id: payload.user_id } : null) ||
        (payload.username ? { username: payload.username } : null);

    if (!candidate) return null;

    const hasIdentity = Boolean(candidate.id || candidate.user_id || candidate.username);
    return hasIdentity ? candidate : null;
}

const AuthContext = createContext({
    user: null,
    authenticated: false,
    initializing: true,
    authError: "",
    login: async () => null,
    logout: async () => {},
    refresh: async () => {},
    markUnauthorized: () => {},
});

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [initializing, setInitializing] = useState(true);
    const [authError, setAuthError] = useState("");

    const clearAuth = useCallback((message = "") => {
        setUser(null);
        setAuthError(message);
        setInitializing(false);
    }, []);

    const markUnauthorized = useCallback(() => {
        clearAuth("Session expired. Please sign in again.");
    }, [clearAuth]);

    const refresh = useCallback(
        async (options = {}) => {
            const { silent = false } = options;
            if (!silent) {
                setInitializing(true);
            }
            try {
                const res = await fetchAuthJson(apiBase(AUTH_ME_PATH));
                const payload = res?.data || {};
                const unauthorized =
                    res?.unauth || payload?.ok === false || payload?.error === "unauthorized" || res?.ok === false;

                if (unauthorized) {
                    clearAuth("Session expired. Please sign in again.");
                    return null;
                }

                const derivedUser = deriveUser(payload);
                if (!derivedUser) {
                    clearAuth("");
                    return null;
                }

                setUser(derivedUser);
                setAuthError("");
                return derivedUser;
            } catch (error) {
                if (!silent) console.warn("[auth] Error refreshing session", error);
                clearAuth("Session unavailable. Please sign in again.");
            } finally {
                if (!silent) {
                    setInitializing(false);
                }
            }
            return null;
        },
        [],
    );

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(
        async (username, password) => {
            const res = await apiLogin(username, password);
            if (!res.ok) {
                throw new Error(res.error || "Invalid credentials");
            }

            const derivedUser = deriveUser(res.data) || { username };
            setUser(derivedUser);
            setAuthError("");
            setInitializing(false);

            refresh({ silent: true }).catch((error) => {
                console.warn("[auth] Background refresh failed after login", error);
            });

            return derivedUser;
        },
        [refresh],
    );

    const logout = useCallback(
        async (options = {}) => {
            const { skipRequest = false } = options;
            if (!skipRequest) {
                try {
                    await fetchAuthJson(apiBase(AUTH_LOGOUT_PATH), { method: "POST" });
                } catch {
                    /* noop */
                }
            }
            clearAuth("");
        },
        [clearAuth],
    );

    const value = useMemo(
        () => ({
            user,
            authenticated: Boolean(user?.id || user?.user_id || user?.username),
            initializing,
            authError,
            login,
            logout,
            refresh,
            markUnauthorized,
        }),
        [user, initializing, authError, login, logout, refresh, markUnauthorized],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export function useAuth() {
    return useContext(AuthContext);
}
